import { Bot, CornerDownRight, Loader2, RadioTower, Shield, Sparkles } from "lucide-react";
import { clsx } from "clsx";
import { useCallback, useEffect, useState } from "react";
import { providerLabel } from "../../lib/ai-pwa";
import {
  elapsedLabel,
  hasActiveJobs,
  statusLabel,
  statusTone,
  StatusTone,
  jobDisplayTitle,
} from "../../lib/agentJobs";
import { SquadItem } from "../../lib/commandCore";
import {
  chainLabel,
  DecisionAction,
  suggestedDecisionAction,
} from "../../lib/delegationCore";
import {
  buildExecutionCard,
  cleanAgentOutput,
  findSimilarMissionTitle,
  isWeakMissionTitle,
  missionTitleFromOutput,
} from "../../lib/missionExecutionCard";
import { AgentJob, AgentPreset, AgentProvider, api, isTauriEnvironment } from "../../lib/tauri";
import { useTaskStore } from "../../stores/taskStore";
import { useUiStore } from "../../stores/uiStore";
import { AiSquadQueue } from "./AiSquadQueue";
import { PresetPicker } from "./PresetPicker";
import { todayIso } from "../../lib/laneCore";

const toneClasses: Record<StatusTone, string> = {
  neutral: "border-border bg-bg text-muted dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300",
  active: "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-100",
  good: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-100",
  watch: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-100",
  danger: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-400/25 dark:bg-rose-400/10 dark:text-rose-100",
};

const badgeClasses: Record<StatusTone, string> = {
  neutral: "bg-hover text-muted dark:bg-zinc-700/60 dark:text-zinc-200",
  active: "bg-indigo-100 text-indigo-800 dark:bg-indigo-400/25 dark:text-indigo-100",
  good: "bg-emerald-100 text-emerald-800 dark:bg-emerald-400/25 dark:text-emerald-100",
  watch: "bg-amber-100 text-amber-800 dark:bg-amber-400/25 dark:text-amber-100",
  danger: "bg-rose-100 text-rose-800 dark:bg-rose-400/25 dark:text-rose-100",
};

const providerLabels: Record<string, string> = { codex: providerLabel("codex"), claude: providerLabel("claude") };

const MAX_VISIBLE_JOBS = 8;

export function AgentBoard({ squad }: { squad: SquadItem[] }) {
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [savingJobId, setSavingJobId] = useState<string | null>(null);
  const createTask = useTaskStore((s) => s.createTask);
  const allTasks = useTaskStore((s) => s.allTasks);
  const showToast = useUiStore((s) => s.showToast);

  const loadJobs = useCallback(async () => {
    try {
      const rows = await api.listAgentJobs?.(20);
      setJobs(rows ?? []);
    } catch (error) {
      // PWA 等でメソッドが無い環境では空表示にフォールバック
      console.error("[Flow] listAgentJobs failed", error);
      setJobs([]);
    }
  }, []);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  // flow://tasks-changed と同じ listen パターン（App.tsx:66-82 の前例）
  useEffect(() => {
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://agent-jobs-changed", () => {
        void loadJobs();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [loadJobs]);

  // 実行中ジョブがある間だけ1秒tickで経過時間を更新
  useEffect(() => {
    if (!hasActiveJobs(jobs)) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [jobs]);

  const activeCount = jobs.filter((j) => j.status === "running" || j.status === "queued").length;
  const visibleJobs = jobs.slice(0, MAX_VISIBLE_JOBS);

  const saveAsTask = async (job: AgentJob) => {
    const cleaned = cleanAgentOutput(job.result);
    const title = missionTitleFromOutput(cleaned);
    if (isWeakMissionTitle(title)) {
      showToast("結果からMissionタイトルを作れませんでした");
      return;
    }
    const today = todayIso();
    const similar = findSimilarMissionTitle(title, allTasks, today);
    if (similar?.exact) {
      showToast(`同じMissionが既にあります: ${similar.title}`);
      return;
    }
    setSavingJobId(job.id);
    try {
      await createTask({
        title,
        description: buildExecutionCard(cleaned, null),
        priority: job.status === "needs_decision" ? "high" : "medium",
        dueDate: today,
      });
      setSavedJobIds((prev) => new Set(prev).add(job.id));
      showToast("今日のMission一覧に追加しました");
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error));
    } finally {
      setSavingJobId(null);
    }
  };

  // 設計判断9: セッション内の二重押下ガード（decisions は追記型ログなので再起動後の再押下は許容）
  const [decidedJobs, setDecidedJobs] = useState<Record<string, DecisionAction>>({});
  const [decidingJobId, setDecidingJobId] = useState<string | null>(null);
  const [followupProvider, setFollowupProvider] = useState<Record<string, AgentProvider>>({});
  const [followingJobId, setFollowingJobId] = useState<string | null>(null);
  const [followupPreset, setFollowupPreset] = useState<Record<string, AgentPreset>>({});
  const [followupModelOverride, setFollowupModelOverride] = useState<Record<string, string>>({});
  const [followupEffortOverride, setFollowupEffortOverride] = useState<Record<string, string>>({});

  const apiErrorToast = (e: unknown, desktopOnlyLabel: string) => {
    // api は Proxy なので ?. では短絡しない（"Flow api method not found" を翻訳する）
    const message = e instanceof Error ? e.message : String(e);
    showToast(
      message.includes("Flow api method not found") ? `${desktopOnlyLabel}はデスクトップ版のみ対応です` : message
    );
  };

  const recordDecision = async (job: AgentJob, action: DecisionAction) => {
    if (decidingJobId !== null) return; // 二重投入防止
    setDecidingJobId(job.id);
    try {
      await api.addDecision?.({
        missionId: job.missionId ?? "",
        action,
        reason: `結果カードから${action}: ${jobDisplayTitle(job)} (${job.provider})`,
      });
      setDecidedJobs((prev) => ({ ...prev, [job.id]: action }));
      showToast(`判断 ${action} を記録しました`);
    } catch (e) {
      apiErrorToast(e, "判断記録");
    } finally {
      setDecidingJobId(null);
    }
  };

  const followUp = async (job: AgentJob) => {
    if (followingJobId !== null) return; // 二重投入防止
    const action = decidedJobs[job.id];
    if (!action) {
      showToast("先に A/B/C/D で判断を選んでください");
      return;
    }
    setFollowingJobId(job.id);
    const fpPreset = followupPreset[job.id] ?? "normal";
    const fpModel = followupModelOverride[job.id] ?? "";
    const fpEffort = followupEffortOverride[job.id] ?? "";
    try {
      await api.enqueueFollowupJob?.({
        parentJobId: job.id,
        provider: followupProvider[job.id] ?? job.provider,
        action,
        preset: fpPreset !== "normal" ? fpPreset : null,
        modelOverride: fpModel || null,
        effortOverride: fpEffort || null,
      });
      showToast("続きの委任を投入しました");
    } catch (e) {
      apiErrorToast(e, "続きを委任");
    } finally {
      setFollowingJobId(null);
    }
  };

  return (
    <section className="command-card">
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-hover text-brand-600 dark:bg-zinc-900 dark:text-indigo-200">
            <RadioTower className="h-4 w-4" />
          </div>
          <div>
            <p className="command-dim text-sm font-semibold">AIチーム</p>
            <h2 className="text-xl font-bold">
              稼働ボード
              {activeCount > 0 && (
                <span className="ml-2 rounded-full bg-indigo-400/20 px-2 py-0.5 text-sm font-semibold text-indigo-200">
                  {activeCount} 稼働中
                </span>
              )}
            </h2>
          </div>
        </div>
      </div>

      <div className="space-y-2 border-t border-border p-4 dark:border-white/10">
        {visibleJobs.length === 0 && (
          <p className="command-subcard p-3 text-sm text-muted dark:text-zinc-400">
            ジョブはまだありません。Command Sheet から「稼働ボードで並列実行」するか、Claude Code から POST
            /agent-jobs で投入できます。
          </p>
        )}
        {visibleJobs.map((job) => {
          const tone = statusTone(job.status);
          const ProviderIcon = job.provider === "codex" ? Bot : Shield;
          const canSave =
            (job.status === "done" || job.status === "needs_decision") &&
            !savedJobIds.has(job.id) &&
            Boolean(job.result.trim());
          const chain = chainLabel(job, jobs);
          const finished = job.status === "done" || job.status === "needs_decision";
          return (
            <div key={job.id} className={`rounded-md border p-3 ${toneClasses[tone]}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-black/5 dark:bg-black/20">
                  {job.status === "running" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ProviderIcon className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold">{jobDisplayTitle(job)}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeClasses[tone]}`}>
                      {statusLabel(job.status)}
                    </span>
                    {chain && (
                      <span className="shrink-0 rounded-full bg-black/5 px-2 py-0.5 text-[11px] text-muted dark:bg-black/25 dark:text-zinc-300">
                        {chain}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs opacity-80">
                    {providerLabels[job.provider] ?? job.provider}
                    {job.modelHint && `・${job.modelHint}`}
                    {job.effortHint && `・${job.effortHint}`}
                    {" ・ "}{elapsedLabel(job, now)}
                    {job.attempts > 1 && " ・ リトライ済"}
                    {job.status === "failed" && job.error && ` ・ ${job.error.slice(0, 60)}`}
                  </p>
                </div>
                {canSave && (
                  <button
                    type="button"
                    disabled={savingJobId === job.id}
                    onClick={() => void saveAsTask(job)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-current/25 bg-white/40 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/70 disabled:opacity-50 dark:bg-black/20 dark:hover:bg-black/40"
                  >
                    {savingJobId === job.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    タスク化
                  </button>
                )}
                {savedJobIds.has(job.id) && (
                  <span className="shrink-0 text-xs font-semibold text-emerald-200">追加済み</span>
                )}
              </div>
              {finished && (
                <JobResultActions
                  job={job}
                  decidedAction={decidedJobs[job.id] ?? null}
                  deciding={decidingJobId === job.id}
                  following={followingJobId === job.id}
                  provider={followupProvider[job.id] ?? job.provider}
                  preset={followupPreset[job.id] ?? "normal"}
                  modelOverride={followupModelOverride[job.id] ?? ""}
                  effortOverride={followupEffortOverride[job.id] ?? ""}
                  onDecide={(action) => void recordDecision(job, action)}
                  onProviderChange={(p) => {
                    setFollowupProvider((prev) => ({ ...prev, [job.id]: p }));
                    // M2: provider 切替時に override を残留させない（不正値400・非表示残留の防止）
                    setFollowupModelOverride((prev) => ({ ...prev, [job.id]: "" }));
                    setFollowupEffortOverride((prev) => ({ ...prev, [job.id]: "" }));
                  }}
                  onPresetChange={(p) =>
                    setFollowupPreset((prev) => ({ ...prev, [job.id]: p }))
                  }
                  onModelOverrideChange={(m) =>
                    setFollowupModelOverride((prev) => ({ ...prev, [job.id]: m }))
                  }
                  onEffortOverrideChange={(e) =>
                    setFollowupEffortOverride((prev) => ({ ...prev, [job.id]: e }))
                  }
                  onFollowUp={() => void followUp(job)}
                />
              )}
            </div>
          );
        })}
        {jobs.length > MAX_VISIBLE_JOBS && (
          <p className="command-faint text-xs">他 {jobs.length - MAX_VISIBLE_JOBS} 件の履歴あり</p>
        )}
      </div>

      {/* 置き換え拡張: 既存の委任先キュー（キーワードベース表示）は折りたたみのまま内包する */}
      <div className="border-t border-border dark:border-white/10">
        <AiSquadQueue squad={squad} />
      </div>
    </section>
  );
}

const ACTION_LABELS: Record<DecisionAction, string> = {
  A: "A 調査",
  B: "B 案だけ",
  C: "C 実行準備",
  D: "D 保留",
};

function JobResultActions({
  job,
  decidedAction,
  deciding,
  following,
  provider,
  preset,
  modelOverride,
  effortOverride,
  onDecide,
  onProviderChange,
  onPresetChange,
  onModelOverrideChange,
  onEffortOverrideChange,
  onFollowUp,
}: {
  job: AgentJob;
  decidedAction: DecisionAction | null;
  deciding: boolean;
  following: boolean;
  provider: AgentProvider;
  preset: AgentPreset;
  modelOverride: string;
  effortOverride: string;
  onDecide: (action: DecisionAction) => void;
  onProviderChange: (provider: AgentProvider) => void;
  onPresetChange: (p: AgentPreset) => void;
  onModelOverrideChange: (m: string) => void;
  onEffortOverrideChange: (e: string) => void;
  onFollowUp: () => void;
}) {
  // needs_decision は AI が提案した action をデフォルト強調（infer_decision_action と同じ規約）
  const suggested = suggestedDecisionAction(job);
  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-current/20 pt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {(["A", "B", "C", "D"] as const).map((action) => {
          const isDecided = decidedAction === action;
          const isSuggested = suggested === action && !decidedAction;
          return (
            <button
              key={action}
              type="button"
              disabled={deciding || decidedAction !== null}
              onClick={() => onDecide(action)}
              className={clsx(
                "rounded-md border px-2 py-1 text-[11px] font-semibold disabled:opacity-60",
                isDecided
                  ? "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-400/60 dark:bg-emerald-400/20 dark:text-emerald-100"
                  : isSuggested
                    ? "border-indigo-300 bg-indigo-100 text-indigo-800 ring-1 ring-indigo-300/60 dark:border-indigo-300/70 dark:bg-indigo-400/25 dark:text-indigo-100"
                    : "border-current/20 bg-white/40 text-current hover:bg-white/70 dark:border-white/15 dark:bg-black/20 dark:text-zinc-200 dark:hover:bg-black/40"
              )}
            >
              {ACTION_LABELS[action]}
              {isSuggested && <span className="ml-1 text-[10px] opacity-80">AI案</span>}
            </button>
          );
        })}
      </div>
      {/* 続きを委任: プロバイダー + プリセット + 実行ボタン */}
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex flex-col gap-1">
          <select
            aria-label="続きの委任先"
            value={provider}
            onChange={(e) => onProviderChange(e.target.value as AgentProvider)}
            className="rounded border border-current/20 bg-white/40 px-1.5 py-1 text-[11px] text-current dark:border-white/15 dark:bg-black/30 dark:text-zinc-200"
          >
            <option value="codex">{providerLabel("codex")}</option>
            <option value="claude">{providerLabel("claude")}</option>
          </select>
          <div className="min-w-[160px]">
            <PresetPicker
              provider={provider}
              preset={preset}
              modelOverride={modelOverride}
              effortOverride={effortOverride}
              onPresetChange={onPresetChange}
              onModelOverrideChange={onModelOverrideChange}
              onEffortOverrideChange={onEffortOverrideChange}
            />
          </div>
        </div>
        <button
          type="button"
          disabled={following || !decidedAction}
          onClick={onFollowUp}
          title={decidedAction ? "選んだ判断で続きを委任" : "先に A/B/C/D を選ぶ"}
          className="inline-flex items-center gap-1 rounded-md border border-current/20 bg-white/40 px-2.5 py-1 text-[11px] font-semibold text-current hover:bg-white/70 disabled:opacity-50 dark:border-white/15 dark:bg-black/20 dark:text-zinc-100 dark:hover:bg-black/40"
        >
          {following ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <CornerDownRight className="h-3 w-3" />
          )}
          続きを委任
        </button>
      </div>
    </div>
  );
}
